import React, { useState, useEffect, useCallback } from "react";
import { FiUserPlus, FiUserX, FiShield, FiEye, FiPlus, FiRefreshCw } from "react-icons/fi";
import {
  getVaultPermissions,
  getAvailableSubUsers,
  grantVaultPermission,
  revokeVaultPermission,
  VaultPermission,
  SubUser
} from "../../utils/api/endpoints/vault";

interface VaultPermissionManagerProps {
  vaultId: number;
  vaultName: string;
  onClose: () => void;
}

// Role display names
// Note: Security Engineer (role 6) is excluded from vault permissions
const getRoleName = (role: number): string => {
  switch (role) {
    case 7: return "Program Manager";
    case 8: return "Organisation Admin";
    default: return "Team Member";
  }
};

// Default permissions based on role
// Note: can_link is removed as sub-users cannot link vaults (requires master password)
// Note: Security Engineer (DEVELOPER role 6) is excluded - they don't have vault access
const getDefaultPermissions = (role: number) => {
  switch (role) {
    case 7: // BUSINESS_MANAGER - Program Manager
      return { can_add: false, can_view_audit: true };
    case 8: // BUSINESS_ADMINISTRATOR - Org Admin
      return { can_add: true, can_view_audit: true };
    default:
      return { can_add: false, can_view_audit: false };
  }
};

const VaultPermissionManager: React.FC<VaultPermissionManagerProps> = ({
  vaultId,
  vaultName,
  onClose
}) => {
  const [permissions, setPermissions] = useState<VaultPermission[]>([]);
  const [availableUsers, setAvailableUsers] = useState<SubUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGranting, setIsGranting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [customPermissions, setCustomPermissions] = useState({
    can_add: false,
    can_view_audit: false
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState<{ permissionId: string; userName: string } | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [permRes, usersRes] = await Promise.all([
        getVaultPermissions(vaultId),
        getAvailableSubUsers(vaultId)
      ]);
      setPermissions(permRes.permissions || []);
      setAvailableUsers(usersRes.subUsers || []);
    } catch (error) {
      console.error("Error fetching permission data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [vaultId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUserSelect = (userId: number) => {
    setSelectedUser(userId);
    const user = availableUsers.find(u => u.user_id === userId);
    if (user) {
      setCustomPermissions(getDefaultPermissions(user.role));
    }
  };

  const handleGrantPermission = async () => {
    if (!selectedUser) return;
    setIsGranting(true);
    try {
      await grantVaultPermission(vaultId, selectedUser, customPermissions);
      setSelectedUser(null);
      setShowAddForm(false);
      setCustomPermissions({ can_add: false, can_view_audit: false });
      await fetchData();
    } catch (error) {
      console.error("Error granting permission:", error);
    } finally {
      setIsGranting(false);
    }
  };

  const handleRevokeClick = (permissionId: string, userName: string) => {
    setRevokeConfirm({ permissionId, userName });
  };

  const handleRevokePermission = async () => {
    if (!revokeConfirm) return;
    try {
      await revokeVaultPermission(vaultId, revokeConfirm.permissionId);
      await fetchData();
    } catch (error) {
      console.error("Error revoking permission:", error);
    } finally {
      setRevokeConfirm(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Dark Header */}
        <div className="px-6 py-4 bg-slate-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-700 rounded-lg">
                <FiShield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Manage Permissions</h2>
                <p className="text-slate-400 text-sm">Vault: {vaultName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-slate-700 transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <FiRefreshCw className="animate-spin text-green-600 text-2xl" />
            </div>
          ) : (
            <>
              {/* Add Permission Button */}
              {!showAddForm && availableUsers.length > 0 && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg
                           text-gray-500 hover:text-green-600 hover:border-green-400
                           transition-colors flex items-center justify-center gap-2"
                >
                  <FiUserPlus />
                  Add Team Member
                </button>
              )}

              {/* Add Permission Form */}
              {showAddForm && (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h3 className="text-gray-900 font-medium mb-4">Grant Permission</h3>

                  {/* User Selection */}
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Select Team Member</label>
                    <select
                      value={selectedUser || ""}
                      onChange={(e) => handleUserSelect(Number(e.target.value))}
                      className="w-full bg-gray-50 text-gray-900 rounded-lg px-4 py-3 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-500 outline-none focus:bg-white"
                    >
                      <option value="">Choose a team member...</option>
                      {availableUsers.map(user => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.username} ({user.email}) - {getRoleName(user.role)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Permission Checkboxes */}
                  {selectedUser && (
                    <div className="mb-4 space-y-3">
                      <label className="block text-gray-700 text-sm font-medium mb-2">Permissions</label>
                      <label className="flex items-center gap-3 text-gray-900 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={customPermissions.can_add}
                          onChange={(e) => setCustomPermissions(p => ({ ...p, can_add: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div className="p-1.5 bg-slate-700 rounded">
                          <FiPlus className="text-white w-3 h-3" />
                        </div>
                        <span>Can Add Credentials</span>
                        <span className="text-gray-500 text-xs">(Write-only, cannot decrypt)</span>
                      </label>
                      <label className="flex items-center gap-3 text-gray-900 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={customPermissions.can_view_audit}
                          onChange={(e) => setCustomPermissions(p => ({ ...p, can_view_audit: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div className="p-1.5 bg-slate-700 rounded">
                          <FiEye className="text-white w-3 h-3" />
                        </div>
                        <span>Can View Audit Trail</span>
                      </label>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setSelectedUser(null);
                      }}
                      className="px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGrantPermission}
                      disabled={!selectedUser || isGranting}
                      className="px-5 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600
                               disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                      {isGranting ? <FiRefreshCw className="animate-spin" /> : <FiUserPlus />}
                      Grant Permission
                    </button>
                  </div>
                </div>
              )}

              {/* Current Permissions List */}
              <div>
                <h3 className="text-gray-900 font-medium mb-4">Current Permissions</h3>
                {permissions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No team members have been granted access to this vault yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {permissions.map(perm => (
                      <div
                        key={perm.permission_id}
                        className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          {perm.user?.pfp ? (
                            <img
                              src={perm.user.pfp}
                              alt={perm.user.username}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium">
                              {perm.user?.username?.charAt(0).toUpperCase() || "?"}
                            </div>
                          )}
                          <div>
                            <p className="text-gray-900 font-medium">{perm.user?.username}</p>
                            <p className="text-gray-500 text-sm">{perm.user?.email}</p>
                            <p className="text-gray-400 text-xs">{getRoleName(perm.user?.role || 0)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex gap-2">
                            {perm.can_add && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-lg flex items-center gap-1">
                                <FiPlus size={12} /> Add
                              </span>
                            )}

                            {perm.can_view_audit && (
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-lg flex items-center gap-1">
                                <FiEye size={12} /> Audit
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRevokeClick(perm.permission_id, perm.user?.username || "this user")}
                            className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Revoke Permission"
                          >
                            <FiUserX />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* No Available Users Message */}
              {availableUsers.length === 0 && !showAddForm && (
                <p className="text-gray-500 text-center py-4 mt-4 border-t border-gray-200">
                  All team members already have permissions for this vault.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end bg-teal-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
          >
            Done
          </button>
        </div>
      </div>

      {/* Revoke Confirmation Dialog */}
      {revokeConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl overflow-hidden max-w-md w-full mx-4 shadow-xl">
            <div className="px-6 py-4 bg-slate-800">
              <h3 className="text-lg font-semibold text-white">Revoke Permission</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to revoke permission for <span className="text-gray-900 font-medium">{revokeConfirm.userName}</span>?
                They will no longer be able to access this vault.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-teal-50">
              <button
                onClick={() => setRevokeConfirm(null)}
                className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokePermission}
                className="px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaultPermissionManager;

